import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Trash2, Users, User, Check, Pencil, BarChart3 } from "lucide-react";

export function PresetGovernance() {
  const [presets, setPresets] = useState(null);
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");

  const load = () => api.get("/admin/band-presets").then((r) => setPresets(r.data)).catch(() => setPresets([]));
  useEffect(() => { load(); }, []);

  const toggleShare = async (p) => {
    try {
      await api.patch(`/admin/band-presets/${p.id}`, { shared: !p.shared });
      toast.success(p.shared ? "Preset made personal" : "Preset shared with all users");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const saveName = async (p) => {
    const name = editName.trim();
    if (!name) return toast.error("Name required");
    try {
      await api.patch(`/admin/band-presets/${p.id}`, { name });
      setEditId(null); toast.success("Preset renamed"); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const del = async (p) => {
    try { await api.delete(`/admin/band-presets/${p.id}`); toast.info(`Deleted "${p.name}"`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <Card className="p-5 shadow-none" data-testid="preset-governance">
      <div className="mb-3">
        <h3 className="font-display font-semibold">Band Presets Governance</h3>
        <p className="text-xs text-muted-foreground mt-1">Every band-ratio recipe saved across the team. Rename, share platform-wide or remove stale recipes.</p>
      </div>
      {presets === null ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : presets.length === 0 ? (
        <div className="text-sm text-muted-foreground" data-testid="presets-empty">No band presets have been saved yet.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[700px]">
            <thead><tr className="text-left text-muted-foreground overline border-b border-border">
              <th className="px-3 py-2 font-medium">Preset</th>
              <th className="px-3 py-2 font-medium">Commodity</th>
              <th className="px-3 py-2 font-medium">Owner</th>
              <th className="px-3 py-2 font-medium">Uses</th>
              <th className="px-3 py-2 font-medium">Visibility</th>
              <th className="px-3 py-2 font-medium text-right">Actions</th>
            </tr></thead>
            <tbody>
              {presets.map((p) => (
                <tr key={p.id} className="border-b border-border/60" data-testid={`gov-preset-${p.id}`}>
                  <td className="px-3 py-2">
                    {editId === p.id ? (
                      <div className="flex items-center gap-1.5">
                        <Input value={editName} onChange={(e) => setEditName(e.target.value)} className="h-8 w-48" data-testid={`gov-name-input-${p.id}`} />
                        <Button size="sm" className="h-8" onClick={() => saveName(p)} data-testid={`gov-name-save-${p.id}`}><Check size={14} /></Button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5">
                        <span className="font-medium">{p.name}</span>
                        <button className="text-muted-foreground hover:text-foreground" onClick={() => { setEditId(p.id); setEditName(p.name); }} data-testid={`gov-rename-${p.id}`}><Pencil size={12} /></button>
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2"><Badge variant="secondary" className="font-mono text-[10px]">{p.commodity}</Badge></td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">{p.owner_name || "-"}</td>
                  <td className="px-3 py-2">
                    <span className="inline-flex items-center gap-1 font-mono font-semibold text-slate-700" data-testid={`gov-uses-${p.id}`} title={p.last_used_at ? `Last used ${new Date(p.last_used_at).toLocaleDateString()}` : "Never used"}>
                      <BarChart3 size={12} className="text-orange-500" /> {p.uses || 0}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <button onClick={() => toggleShare(p)} data-testid={`gov-share-${p.id}`}
                      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold border transition-colors ${p.shared ? "bg-orange-50 text-orange-700 border-orange-200" : "bg-slate-100 text-slate-600 border-slate-200"}`}>
                      {p.shared ? <Users size={11} /> : <User size={11} />} {p.shared ? "Team" : "Personal"}
                    </button>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Button variant="ghost" size="sm" className="h-8 text-destructive hover:bg-destructive/10 hover:text-destructive" onClick={() => del(p)} data-testid={`gov-delete-${p.id}`}><Trash2 size={14} /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
